import { useEffect, useMemo, useRef, useState } from 'react';

import { useSearchParams } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';

import { Col, Divider, Input, Row, Tag, Typography } from 'antd';

import { collectionMap, collectionSlugToPathMap } from '../const/CollectionsInfo';
import { useWalletInventory, isValidAddress } from '../hooks/useWalletInventory';
import NftCard, { capitalizeString } from '../components/NftCard';
import { EmptyState, ErrorState, LoadingGrid } from '../components/Feedback';

const { Search } = Input;
const { Text } = Typography;

const LAST_WALLET_KEY = 'duckly:lastWallet';

const readStoredWallet = (): string => {
    try {
        return localStorage.getItem(LAST_WALLET_KEY) || '';
    } catch {
        return '';
    }
};

const storeWallet = (address: string) => {
    try {
        localStorage.setItem(LAST_WALLET_KEY, address);
    } catch {
        // private mode — non-essential
    }
};

const WalletPage = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const { inventory, loading, error, search } = useWalletInventory();
    const [inputValue, setInputValue] = useState('');
    const [activeAddress, setActiveAddress] = useState('');
    const initialized = useRef(false);

    const isTabletOrMobile = useMediaQuery({ query: '(max-width: 991px)' });

    const runSearch = (address: string) => {
        const trimmed = address.trim();
        if (!isValidAddress(trimmed)) return;
        setActiveAddress(trimmed);
        storeWallet(trimmed);
        setSearchParams({ address: trimmed }, { replace: true });
        search(trimmed);
    };

    // On first visit, restore the address from the URL (shareable links) or the last search.
    useEffect(() => {
        if (initialized.current) return;
        initialized.current = true;
        const fromUrl = searchParams.get('address') || '';
        const candidate = isValidAddress(fromUrl) ? fromUrl : readStoredWallet();
        if (isValidAddress(candidate)) {
            setInputValue(candidate);
            runSearch(candidate);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const totalItems = useMemo(
        () => (inventory ? Object.values(inventory).reduce((sum, nfts) => sum + nfts.length, 0) : 0),
        [inventory]
    );

    const inputInvalid = inputValue.trim().length > 0 && !isValidAddress(inputValue);

    return (
        <div style={{ padding: isTabletOrMobile ? '12px' : '20px 32px', maxWidth: 1600, width: '100%', margin: '0 auto', boxSizing: 'border-box' }}>
            <Row justify='center' style={{ marginBottom: 8 }}>
                <Col xs={24} lg={14}>
                    <Search
                        placeholder='Paste your wallet address (0x…)'
                        enterButton='Search'
                        size='large'
                        value={inputValue}
                        onChange={(event) => setInputValue(event.target.value)}
                        status={inputInvalid ? 'error' : ''}
                        onSearch={runSearch}
                        loading={loading}
                    />
                    {inputInvalid && (
                        <Text type='danger' style={{ fontSize: 12 }}>
                            That doesn't look like a valid wallet address.
                        </Text>
                    )}
                </Col>
            </Row>

            {error ? (
                <ErrorState message={error} onRetry={() => activeAddress && search(activeAddress)} />
            ) : loading ? (
                <LoadingGrid />
            ) : inventory == null ? (
                <EmptyState description='Search a wallet to see its MyDuckly NFTs.' />
            ) : totalItems === 0 ? (
                <EmptyState description='No MyDuckly NFTs found in this wallet.' />
            ) : (
                <>
                    <Text style={{ opacity: 0.7 }}>
                        {totalItems} item{totalItems === 1 ? '' : 's'} across {Object.keys(inventory).length} group
                        {Object.keys(inventory).length === 1 ? '' : 's'}
                    </Text>
                    {Object.entries(inventory).map(([terrain, nfts]) => (
                        <div key={terrain}>
                            <Divider orientation='left' style={{ marginBottom: 12 }}>
                                {capitalizeString(terrain)} <Tag style={{ marginLeft: 6 }}>{nfts.length}</Tag>
                            </Divider>
                            <div className='nft-grid'>
                                {nfts.map((nft) => {
                                    const path = collectionSlugToPathMap[nft.collection];
                                    const info = path ? collectionMap[path] : undefined;
                                    return (
                                        <NftCard
                                            key={`${nft.collection}_${nft.identifier}`}
                                            nft={nft}
                                            listing={null}
                                            collectionName={info?.collectionName || capitalizeString(nft.collection)}
                                            collectionPath={path || nft.collection}
                                            statsNames={info?.statsNames || []}
                                            boostsNames={info?.boostsNames || []}
                                        />
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </>
            )}
        </div>
    );
};

export default WalletPage;
